import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
    fetchLearningPaths,
    createLearningPath,
    markDayCompleted,
    deleteLearningPath,
} from "../../services/LearningPathService";

/**
 * LearningPathModal — Modal lộ trình học cá nhân.
 */
function LearningPathModal({ open, onClose, onStartDay, examTarget }) {
    const [paths, setPaths] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [selectedPath, setSelectedPath] = useState(null);
    const [duration, setDuration] = useState(7);

    const loadPaths = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchLearningPaths({ limit: 20 });
            setPaths(res?.data || []);
        } catch (e) {
            console.error("loadPaths:", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) loadPaths();
    }, [open, loadPaths]);

    const handleCreate = async () => {
        setCreating(true);
        try {
            // Tạo placeholder — nội dung sẽ được AI generate qua chat
            const path = await createLearningPath({
                title: `Lộ trình ${duration} ngày`,
                examTarget: examTarget || "thpt",
                durationDays: duration,
                summary: "",
                planDays: [],
            });
            toast.success("Đã tạo lộ trình! Hãy để PIMATH lên kế hoạch cho bạn.");
            setPaths((prev) => [path, ...prev]);
            setSelectedPath(path);

            // Trigger AI to generate the plan
            const prompt = `Hãy tạo lộ trình học Toán ${duration} ngày cho tôi. Mục tiêu: ${examTarget || "THPT"}. Với mỗi ngày, hãy nêu: mục tiêu, chủ đề cần học, bài tập cần làm, và một câu hỏi luyện tập. Trình bày theo format:

**Ngày 1:** [Mục tiêu]
- Chủ đề: ...
- Bài tập: ...
- Luyện tập: ...

(Tương tự cho các ngày còn lại)`;
            onStartDay?.(prompt);
            onClose();
        } catch (e) {
            toast.error("Lỗi tạo lộ trình");
        } finally {
            setCreating(false);
        }
    };

    const handleMarkDay = async (pathId, day) => {
        try {
            await markDayCompleted(pathId, day);
            toast.success(`Đã hoàn thành ngày ${day}!`);
            loadPaths();
        } catch (e) {
            toast.error("Lỗi cập nhật");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Xóa lộ trình này?")) return;
        try {
            await deleteLearningPath(id);
            toast.success("Đã xóa");
            if (selectedPath?._id === id) setSelectedPath(null);
            loadPaths();
        } catch (e) {
            toast.error("Lỗi xóa");
        }
    };

    const handleStartDay = (path, day) => {
        const prompt = day.practicePrompt || `Hãy cho tôi bài tập về: ${day.topics?.join(", ") || day.goal}. Mục tiêu hôm nay: ${day.goal}`;
        onStartDay?.(prompt);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                style={{ animation: "pimSlideUp 0.15s ease-out" }}
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <span className="text-lg">🗺️</span>
                        <h2 className="text-base font-bold text-gray-900">Lộ trình học</h2>
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Create new */}
                <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600">Tạo lộ trình mới:</span>
                        <div className="flex gap-1.5">
                            {[7, 14, 30].map((d) => (
                                <button
                                    key={d}
                                    type="button"
                                    onClick={() => setDuration(d)}
                                    className={`px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors ${duration === d
                                            ? "border-red-500 bg-red-50 text-red-700"
                                            : "border-gray-200 text-gray-600 hover:bg-gray-50"
                                        }`}
                                >
                                    {d} ngày
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={handleCreate}
                            disabled={creating}
                            className="ml-auto px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
                        >
                            {creating ? "Đang tạo..." : "Tạo"}
                        </button>
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto px-5 py-3 min-h-0">
                    {loading ? (
                        <div className="text-center py-8 text-gray-500 text-sm">Đang tải...</div>
                    ) : paths.length === 0 ? (
                        <div className="text-center py-8">
                            <span className="text-3xl">🗺️</span>
                            <p className="text-sm text-gray-500 mt-2">Chưa có lộ trình nào.</p>
                            <p className="text-xs text-gray-400 mt-1">Tạo lộ trình mới để PIMATH lên kế hoạch học cho bạn.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {paths.map((path) => {
                                const completedDays = (path.planDays || []).filter((d) => d.completed).length;
                                const totalDays = path.durationDays || path.planDays?.length || 0;
                                const isExpanded = selectedPath?._id === path._id;

                                return (
                                    <div key={path._id} className="border border-gray-200 rounded-xl overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedPath(isExpanded ? null : path)}
                                            className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate">{path.title}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                    {completedDays}/{totalDays} ngày · {path.examTarget?.toUpperCase()}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${path.status === "completed" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                                                    }`}>
                                                    {path.status === "completed" ? "Hoàn thành" : "Đang học"}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(path._id); }}
                                                    className="p-1 text-gray-400 hover:text-red-500"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </button>

                                        {isExpanded && path.planDays && path.planDays.length > 0 && (
                                            <div className="border-t border-gray-100 px-4 py-2 space-y-1.5 bg-gray-50/50">
                                                {path.planDays.map((day) => (
                                                    <div key={day.day} className="flex items-center gap-2 text-xs">
                                                        <button
                                                            type="button"
                                                            onClick={() => !day.completed && handleMarkDay(path._id, day.day)}
                                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${day.completed
                                                                    ? "border-green-500 bg-green-500 text-white"
                                                                    : "border-gray-300 hover:border-green-400"
                                                                }`}
                                                        >
                                                            {day.completed && (
                                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                        <span className={`flex-1 ${day.completed ? "text-gray-400 line-through" : "text-gray-700"}`}>
                                                            Ngày {day.day}: {day.goal}
                                                        </span>
                                                        {!day.completed && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleStartDay(path, day)}
                                                                className="px-2 py-0.5 text-[10px] font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md"
                                                            >
                                                                Bắt đầu
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default LearningPathModal;
